'use client';

import { createContext, type PropsWithChildren, useContext, useRef } from 'react';
import invariant from 'tiny-invariant';
import { useStore } from 'zustand';

import {
    createSelectedProductStore,
    type SelectedProductState,
    type SelectedProductStore,
} from './selectedProductStore';

type SelectedProductStoreType = ReturnType<typeof createSelectedProductStore>;

const SelectedProductStoreContext = createContext<SelectedProductStoreType | undefined>(undefined);

type ProviderProps = PropsWithChildren & {
    initialState: SelectedProductState;
};

export const SelectedProductStoreProvider = ({ children, initialState }: ProviderProps) => {
    const storeRef = useRef<SelectedProductStoreType | null>(null);

    if (!storeRef.current) {
        storeRef.current = createSelectedProductStore(initialState);
    }

    return <SelectedProductStoreContext value={storeRef.current}>{children}</SelectedProductStoreContext>;
};

export const useSelectedProductStore = <T,>(selector: (store: SelectedProductStore) => T): T => {
    const selectedProductStoreContext = useContext(SelectedProductStoreContext);

    invariant(
        selectedProductStoreContext,
        '`useSelectedProductStore must be used within SelectedProductStoreProvider`'
    );

    return useStore(selectedProductStoreContext, selector);
};
